import React, { useMemo, useState, useEffect } from "react";
import { useHotel } from "../contexts/HotelContext";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import OnboardingTour from "../components/onboarding/OnboardingTour";
import { useOnboardingTour } from "../hooks/useOnboardingTour";
import { getDashboardTourSteps, TOUR_PAGES } from "../config/onboardingTourConfig";
import { getDomainConfig } from "../utils/domainConfig";
import { KFS_CALLS_HREF, BUR_CALLS_HREF } from "../constants/legacyGrantsRoutes";
import {
  PlusIcon,
  DocumentIcon,
  GridIcon,
  UsersIcon,
  ChartBarIcon,
} from "../components/icons";

const Dashboard: React.FC = () => {
  const { user, hasModuleEnabled, isLoading } = useHotel();
  const hasHotelAccess = hasModuleEnabled("hotel:offers");
  const hasGrantsAccess = hasModuleEnabled("grants:schedule");
  const hasGrantsCallsAccess = hasModuleEnabled("grants:calls");
  const { docsUrl } = getDomainConfig();

  // Detect mobile viewport (< lg = 1024px) - reactive to resize/rotation
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 1024);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const tourSteps = useMemo(
    () => getDashboardTourSteps({ hasGrantsCalls: hasGrantsCallsAccess, docsUrl, isMobile }),
    [hasGrantsCallsAccess, docsUrl, isMobile]
  );

  const { shouldRunTour, markTourCompleted } = useOnboardingTour({
    pageKey: TOUR_PAGES.dashboard.pageKey,
    version: TOUR_PAGES.dashboard.version,
    userId: user?.id ?? null,
    isLoading,
  });

  return (
    <Layout>
      <OnboardingTour
        shouldRunTour={shouldRunTour}
        steps={tourSteps}
        onComplete={markTourCompleted}
      />
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Kafelki hotelowe - tylko jeśli ma uprawnienia */}
        {hasHotelAccess && (
          <>
            <Link
              to="/offers/create"
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <PlusIcon className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1 text-left">
                  <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-primary-700 transition-colors">
                    Utwórz nową ofertę
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Zacznij budować swoją następną ofertę B2B
                  </p>
                </div>
              </div>
            </Link>

            <Link
              to="/offers"
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 bg-success-100 rounded-lg flex items-center justify-center group-hover:bg-success-200 transition-colors">
                    <DocumentIcon className="h-6 w-6 text-success-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1 text-left">
                  <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-success-700 transition-colors">
                    Zarządzaj ofertami
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Przeglądaj i edytuj wszystkie swoje oferty
                  </p>
                </div>
              </div>
            </Link>

            <Link
              to="/hotel/category-options"
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <GridIcon className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1 text-left">
                  <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-purple-700 transition-colors">
                    Zarządzanie składnikami
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Konfiguruj składniki oferty według kategorii
                  </p>
                </div>
              </div>
            </Link>

            {user?.role === "admin" && (
              <Link
                to="/hotel-settings"
                className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 group"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                      <UsersIcon className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1 text-left">
                    <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-orange-700 transition-colors">
                      Ustawienia hotelu
                    </h3>
                    <p className="text-sm text-neutral-600">
                      Zarządzaj użytkownikami i konfiguracją hotelu
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </>
        )}

        {/* Grants Module - tylko jeśli ma uprawnienia */}
        {hasGrantsAccess && (
          <>
            <Link
              to={KFS_CALLS_HREF}
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <ChartBarIcon className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1 text-left">
                  <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-green-700 transition-colors">
                    Nabory KFS
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Krajowy Fundusz Szkoleniowy
                  </p>
                </div>
              </div>
            </Link>

            <Link
              to={BUR_CALLS_HREF}
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <DocumentIcon className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1 text-left">
                  <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-purple-700 transition-colors">
                    Nabory BUR
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Baza Usług Rozwojowych
                  </p>
                </div>
              </div>
            </Link>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
